import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate, useLocation } from "react-router-dom";
import FacotPaymentUtils from "../utils/facotPaymentUtils";
import "../styles/FacotPayment.css";

const FacotPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [applicationData, setApplicationData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1: First Payment, 2: Second Payment, 3: Complete

  // Payment amounts
  const FIRST_PAYMENT_AMOUNT = 3540; // ₹3,540 (including 18% GST)
  const SECOND_PAYMENT_AMOUNT = 8260; // ₹8,260 (including 18% GST)
  const TOTAL_AMOUNT = 11800; // ₹11,800 total

  useEffect(() => {
    // Get application data from location state or fetch from API
    if (location.state?.applicationData) {
      setApplicationData(location.state.applicationData);
      checkPaymentStatus(location.state.applicationData._id);
    } else {
      setError("No application data found. Please submit the application first.");
    }
  }, [location.state]);

  const checkPaymentStatus = async (applicationId) => {
    const result = await FacotPaymentUtils.checkPaymentStatus(applicationId);
    if (result.success) {
      setPaymentStatus(result.data);
      updateCurrentStep(result.data);
    } else {
      setError(result.error);
    }
  };

  const updateCurrentStep = (status) => {
    setCurrentStep(FacotPaymentUtils.getCurrentStep(status));
  };

  const createPaymentOrder = async (paymentType) => {
    const result = await FacotPaymentUtils.createPaymentOrder(applicationData._id, paymentType);
    if (!result.success) {
      throw new Error(result.error);
    }
    return result.data;
  };

  const handlePayment = async (paymentType) => {
    setLoading(true);
    setError(null);

    try {
      // Check eligibility
      const eligibility = FacotPaymentUtils.checkPaymentEligibility(applicationData);
      if (!eligibility.eligible) {
        throw new Error(eligibility.errors.join(', '));
      }

      // Load Razorpay script
      const scriptLoaded = await FacotPaymentUtils.loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load Razorpay script");
      }

      // Create payment order
      const orderData = await createPaymentOrder(paymentType);
      
      const amount = FacotPaymentUtils.getPaymentAmount(paymentType);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: "INR",
        name: "AIOTA FACOT Application",
        description: `FACOT ${paymentType === "first" ? "First" : "Second"} Payment`,
        order_id: orderData.id,
        handler: async function (response) {
          try {
            await verifyPayment(response, paymentType);
          } catch (error) {
            setError("Payment verification failed: " + error.message);
          }
        },
        prefill: {
          name: applicationData.applicantName,
          email: applicationData.email,
        },
        theme: {
          color: "#0030B6",
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setError(FacotPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentResponse, paymentType) => {
    try {
      // Validate payment response
      if (!FacotPaymentUtils.validatePaymentResponse(paymentResponse)) {
        throw new Error("Invalid payment response");
      }

      const result = await FacotPaymentUtils.verifyPayment(paymentResponse, paymentType, applicationData._id);
      
      if (result.success) {
        setSuccess(`Payment successful! ${paymentType === "first" ? "First" : "Second"} payment completed.`);
        // Refresh payment status
        await checkPaymentStatus(applicationData._id);
        setLoading(false);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setError("Payment verification failed: " + FacotPaymentUtils.getErrorMessage(error));
      setLoading(false);
    }
  };

  const getPaymentStatusText = (status) => FacotPaymentUtils.getPaymentStatusText(status);
  const getApprovalStatusText = (status) => FacotPaymentUtils.getApprovalStatusText(status);

  if (!applicationData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container facot-payment-content-container">
          <section className="facot-payment-section">
            <Container>
              <h2 className="text-center facot-payment-heading mb-4">
                FACOT Payment Portal
              </h2>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              {/* Application Details */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Application Details</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Applicant Name:</strong> {applicationData.applicantName}</p>
                      <p><strong>Email:</strong> {applicationData.email}</p>
                      <p><strong>AIOTA Membership No:</strong> {applicationData.aiotaMembershipNo}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>ACOT Registration No:</strong> {applicationData.acotRegistrationNo}</p>
                      <p><strong>Application Date:</strong> {new Date(applicationData.createdAt).toLocaleDateString()}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Progress */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Payment Progress</h4>
                </Card.Header>
                <Card.Body>
                  <div className="payment-progress">
                    <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h6>First Payment</h6>
                        <p>₹{FIRST_PAYMENT_AMOUNT.toLocaleString()}</p>
                        {paymentStatus && (
                          <div className="status-info">
                            <span className={`status-badge ${paymentStatus.paymentfirst_status}`}>
                              {getPaymentStatusText(paymentStatus.paymentfirst_status)}
                            </span>
                            <span className={`status-badge ${paymentStatus.firstapproval_status}`}>
                              {getApprovalStatusText(paymentStatus.firstapproval_status)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h6>Second Payment</h6>
                        <p>₹{SECOND_PAYMENT_AMOUNT.toLocaleString()}</p>
                        {paymentStatus && (
                          <div className="status-info">
                            <span className={`status-badge ${paymentStatus.paymentafter_status}`}>
                              {getPaymentStatusText(paymentStatus.paymentafter_status)}
                            </span>
                            <span className={`status-badge ${paymentStatus.afterapproval_status}`}>
                              {getApprovalStatusText(paymentStatus.afterapproval_status)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h6>Certificate</h6>
                        <p>FACOT Certificate</p>
                        {currentStep >= 3 && (
                          <span className="status-badge completed">Certificate Generated</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Payment Actions */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Payment Actions</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="payment-card">
                        <h5>First Payment</h5>
                        <p className="amount">₹{FIRST_PAYMENT_AMOUNT.toLocaleString()}</p>
                        <p className="description">Processing Fee (including 18% GST)</p>
                        <Button
                          variant="primary"
                          size="lg"
                          className="payment-button"
                          onClick={() => handlePayment("first")}
                          disabled={loading || (paymentStatus?.paymentfirst_status === "completed")}
                        >
                          {loading ? "Processing..." : 
                           paymentStatus?.paymentfirst_status === "completed" ? "Payment Completed" : 
                           "Pay Now"}
                        </Button>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="payment-card">
                        <h5>Second Payment</h5>
                        <p className="amount">₹{SECOND_PAYMENT_AMOUNT.toLocaleString()}</p>
                        <p className="description">Balance Amount (including 18% GST)</p>
                        <Button
                          variant="primary"
                          size="lg"
                          className="payment-button"
                          onClick={() => handlePayment("second")}
                          disabled={loading || 
                            (paymentStatus?.paymentfirst_status !== "completed") ||
                            (paymentStatus?.firstapproval_status !== "approved") ||
                            (paymentStatus?.paymentafter_status === "completed")}
                        >
                          {loading ? "Processing..." : 
                           paymentStatus?.paymentafter_status === "completed" ? "Payment Completed" :
                           paymentStatus?.firstapproval_status !== "approved" ? "Awaiting First Approval" :
                           "Pay Now"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Payment Summary */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Payment Summary</h4>
                </Card.Header>
                <Card.Body>
                  <div className="payment-summary">
                    <div className="summary-row">
                      <span>Processing Fee:</span>
                      <span>₹{FIRST_PAYMENT_AMOUNT.toLocaleString()}</span>
                    </div>
                    <div className="summary-row">
                      <span>Balance Amount:</span>
                      <span>₹{SECOND_PAYMENT_AMOUNT.toLocaleString()}</span>
                    </div>
                    <div className="summary-row total">
                      <span><strong>Total Amount:</strong></span>
                      <span><strong>₹{TOTAL_AMOUNT.toLocaleString()}</strong></span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Important Notes */}
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">Important Notes</h4>
                </Card.Header>
                <Card.Body>
                  <ul className="payment-notes">
                    <li>First payment (₹3,540) must be completed before second payment can be made.</li>
                    <li>Each payment requires secretary approval before proceeding to the next step.</li>
                    <li>FACOT certificate will be generated and sent via email after final approval.</li>
                    <li>All payments are processed securely through Razorpay.</li>
                    <li>For any payment issues, please contact the AIOTA office.</li>
                  </ul>
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              <Row className="d-flex justify-content-center mb-4">
                <Col md={6} className="text-center">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/facot-application-form')}
                    className="me-3"
                  >
                    Back to Application
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Status
                  </Button>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </>
  );
};

export default FacotPayment;
